/**
 * D-074 Faz 5 · PR 26: Project documents belgelerinin ve imzalı OSI datapack'inin okunması (plan M15). Okunan Documents türleri var olan yedi Project documents türüdür (`proje:osi_coc`,
 * `proje:dnv_survey_report`, `proje:dvr_osi`, `proje:kalite_plani`, `proje:prosedur`, `proje:montaj_resmi`, `proje:onceki_cevrim`); yeni tür açılmaz. Datapack ayrı tür değil, CoC ya da DNV
 * satırının okumasıdır. Müşteri siparişi, BOM, malzeme sertifika listesi ve kimlik plakası fotoğrafı okunmaz; Word satırı okunmaz (GEN-16 bugünkü gibi). Okuma yalnız PDF'tedir.
 * Veri ihtiyacı kuralıyla (02.11.2026) yalnız yerin, kuralların ve sayfa önerisinin kullandığı değerler okunur: DOCUMENT'te `Kind`; ROWS'ta `Document` (Kind'e göre alan kümesi), `Sections`
 * (datapack bölümleri), `Parts` (DNV alt parça SN'leri) ve `Marks` (yalnız istisna sayfalar). Basınçlar, MWP'ler, bekleme süreleri, kabul kuralları, kalemler, PN'ler, tarihler (sonraki
 * sörvey dışında) ve kişi adları okunmaz. Sözleşme docs/hafif-excel-sozlesmesi.md §17.
 * Okuma hiçbir kayda kendiliğinden yazmaz: dosya_belge, kural_kaynagi, riser ve hatlar değişmez; sayfa aralığı ve riser yalnız önerilir. Datapack'in öteki türü yalnız kişinin "Also file as
 * {type}" onayıyla bugünkü Project documents bağ yazımından dosyalanır. Datapack MRB dönüştürücüsüne gitmez; plaka fotoğrafı okunmaz (riser kişinindir).
 */
import { UYARI_KALIPLARI } from "./belgeler.js";
import { HIDROTEST_UYARI_KALIPLARI } from "./hidrotest.js";
import { KALIBRASYON_UYARI_KALIPLARI } from "./kalibrasyon.js";
import { NDT_UYARI_KALIPLARI } from "./ndt-raporu.js";
import { kimlikSade } from "./sarf.js";
import { WPS_BELGESI_UYARI_KALIPLARI } from "./wps-belgesi.js";
/** Dönüşüm işinin belge türü (`donusum_isi.belge_turu`). */
export const PROJE_OKUMA_BELGE_TURU = "proje_belgesi";
/** Okunan Documents türleri (M15-1). */
export const PROJE_OKUMA_GELEN_TURLERI = [
    "proje:osi_coc", "proje:dnv_survey_report", "proje:dvr_osi", "proje:kalite_plani", "proje:prosedur", "proje:montaj_resmi", "proje:onceki_cevrim",
];
export const projeOkumaGelenTuruMu = (t) => PROJE_OKUMA_GELEN_TURLERI.includes(t ?? "");
/** Satır türünün Project documents girdi türü ("proje:osi_coc" → "osi_coc"). */
export const projeGirdiTuru = (t) => t.slice("proje:".length);
/** Olay kaydında nesne tipi (nesneId = proje_okuma_kaydi.id). */
export const PROJE_OKUMA_OLAY_NESNESI = "proje_okuma_kaydi";
/** Okunan hafif excel'in belge ailesi. Hiçbir aile sorgusu bu adı almaz (DOSYA_BELGE_AILESI "Project document" başka). */
export const PROJE_OKUMA_GIRDI_AILESI = "Project-document-input";
/** DOCUMENT sayfasındaki "Document type" değeri. */
export const PROJE_OKUMA_EXCEL_ADI = "Project document";
/** Okuma işi kodu ("PROJ-7A1B2C3D"); "PB-" uyarı kodlarından ayrı. */
export const projeOkumaKodu = (kayitId) => `PROJ-${kayitId.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
/** Sections tablosunda en çok satır. */
export const PROJE_BOLUM_EN_COK = 40;
/** Parts tablosunda en çok satır. */
export const PROJE_PARCA_SN_EN_COK = 12;
/** Marks tablosunda en çok satır. */
export const PROJE_ISARET_EN_COK = 100;
/** Kind (M15-6; tanıma). */
export const PROJE_TANIMALARI = ["datapack", "coc", "dnv survey", "dvr", "itp", "procedure", "ga drawing", "previous cycle", "oem mrb", "other"];
/** M15-6: `Kind` değeri (kırpılmış, küçük harf); başka her yazım null. */
export function projeTanimaOku(v) {
    const s = (v ?? "").trim().toLowerCase();
    return PROJE_TANIMALARI.includes(s) ? s : null;
}
/** Kind'in ekran adı (GEN-04 {0}, kart). */
export const PROJE_TANIMA_ADI = {
    datapack: "signed OSI datapack", coc: "OSI Certificate of Conformance", "dnv survey": "DNV survey report", dvr: "DNV design verification report", itp: "inspection and test plan",
    procedure: "procedure", "ga drawing": "general arrangement drawing", "previous cycle": "previous cycle document", "oem mrb": "OEM MRB", other: "another document",
};
/** GEN-04 {1}: Kind'in yeri (oem mrb ve other için null; oem mrb malzeme okumasının metnini kullanır). */
export const PROJE_TANIMA_YERI = {
    datapack: "Project documents › OSI Certificate of Conformance or DNV Survey Report", coc: "Project documents › OSI Certificate of Conformance",
    "dnv survey": "Project documents › DNV Survey Report", dvr: "Project documents › DNV Design Verification Report — Oil States design",
    itp: "Project documents › Quality inspection and test plan", procedure: "Project documents › Procedure", "ga drawing": "Project documents › General arrangement drawing",
    "previous cycle": "Project documents › Previous cycle documents", "oem mrb": null, other: null,
};
/** M15-7: satır türünün kabul ettiği Kind'ler. */
export const PROJE_TUR_TANIMALARI = {
    "proje:osi_coc": ["coc", "datapack"],
    "proje:dnv_survey_report": ["dnv survey", "datapack"],
    "proje:dvr_osi": ["dvr"],
    "proje:kalite_plani": ["itp"],
    "proje:prosedur": ["procedure"],
    "proje:montaj_resmi": ["ga drawing"],
    "proje:onceki_cevrim": ["previous cycle"],
};
/** M15-9: riser kapsamlı Kind'ler (yer önerisi ve KIM yalnız bunlarda). */
export const PROJE_RISER_TANIMALARI = ["datapack", "coc", "dnv survey", "previous cycle"];
export const projeRiserTanimasiMi = (k) => PROJE_RISER_TANIMALARI.includes(k);
/** OEM MRB'nin GEN-04 değerleri (malzeme okumasınınkilerin aynısı). */
export const PROJE_OEM_MRB_GEN04 = ["OEM MRB", "the MRB converter (MRB (Complete PDF))"];
/** DOCUMENT değer alanı (1). Hafif çerçevenin "her alan tam bir kez" kuralı burada bozulmaz; öteki alanlar ROWS'un Document tablosundadır. */
export const PROJE_OKUMA_ALANLARI = [{ alan: "tur_tanima", etiket: "Kind", kimlik: false }];
/** Document tablosunun bütün alanları (sabit sıra; Kind'e göre alt küme PROJE_TANIMA_ALANLARI). Tüketicileri `proje-okuma-karsilastirma.ts`'te (`PROJE_BELGE_OKUNAN`). */
export const PROJE_BELGE_ALANLARI = [
    { alan: "riser_no", etiket: "Riser number", kimlik: true },
    { alan: "riser_sn", etiket: "Riser SN", kimlik: true },
    { alan: "hat_sn_choke", etiket: "Choke SN", kimlik: true },
    { alan: "hat_sn_kill", etiket: "Kill SN", kimlik: true },
    { alan: "hat_sn_booster", etiket: "Booster SN", kimlik: true },
    { alan: "hat_sn_hydraulic", etiket: "Hydraulic SN", kimlik: true },
    { alan: "belge_no", etiket: "Document number", kimlik: true },
    { alan: "rev", etiket: "Revision", kimlik: false },
    { alan: "imza", etiket: "Signed", kimlik: false },
    { alan: "inceleme_surumu", etiket: "For DNV review", kimlik: false },
    { alan: "sonraki_sorvey", etiket: "Next complete survey", kimlik: false },
    { alan: "dvr_no", etiket: "DVR referenced", kimlik: true },
];
/** M15-5: Kind'e göre Document tablosunun alanları (her biri tam bir kez; başka alan reddedilir). */
export const PROJE_TANIMA_ALANLARI = {
    datapack: ["riser_no", "riser_sn", "hat_sn_choke", "hat_sn_kill", "hat_sn_booster", "hat_sn_hydraulic", "inceleme_surumu", "sonraki_sorvey", "dvr_no"],
    coc: ["riser_no", "hat_sn_choke", "hat_sn_kill", "hat_sn_booster", "hat_sn_hydraulic", "belge_no", "imza"],
    "dnv survey": ["riser_no", "belge_no", "rev", "imza", "sonraki_sorvey", "dvr_no"],
    dvr: ["belge_no", "rev"],
    itp: ["belge_no", "rev"],
    procedure: ["belge_no", "rev"],
    "ga drawing": ["belge_no", "rev"],
    "previous cycle": ["riser_no"],
    "oem mrb": [],
    other: [],
};
export const PROJE_TABLOLARI = ["Document", "Sections", "Parts", "Marks"];
/** M15-5: Kind'e göre izinli tablolar. */
export const PROJE_TANIMA_TABLOLARI = {
    datapack: ["Document", "Sections", "Parts", "Marks"],
    coc: ["Document"],
    "dnv survey": ["Document", "Parts"],
    dvr: ["Document"],
    itp: ["Document"],
    procedure: ["Document"],
    "ga drawing": ["Document"],
    "previous cycle": ["Document"],
    "oem mrb": [],
    other: [],
};
/** Sections tablosunun alanları (satır başına 8; sabit sıra). */
export const PROJE_BOLUM_ALANLARI = [
    { alan: "bolum_turu", etiket: "Part kind", kimlik: false },
    { alan: "ilk_sayfa", etiket: "First page", kimlik: false },
    { alan: "son_sayfa", etiket: "Last page", kimlik: false },
    { alan: "baslik", etiket: "Document", kimlik: false },
    { alan: "kapak_riser", etiket: "Riser on cover", kimlik: true },
    { alan: "kapak_sn", etiket: "SN on cover", kimlik: true },
    { alan: "rapor_no", etiket: "Report number", kimlik: true },
    { alan: "rapor_rev", etiket: "Rev", kimlik: false },
];
/** Parts tablosunun alanları (DNV alt parça SN tablosu; satır başına 2). */
export const PROJE_PARCA_SN_ALANLARI = [{ alan: "hat", etiket: "Line", kimlik: false }, { alan: "sn", etiket: "SN", kimlik: true }];
/** Marks tablosunun alanları (yalnız istisna sayfalar; satır başına 3; `Page` zorunlu). */
export const PROJE_ISARET_ALANLARI = [
    { alan: "isaret_riser", etiket: "Riser", kimlik: true },
    { alan: "isaret_sn", etiket: "SN", kimlik: true },
    { alan: "isaret_hat", etiket: "Line", kimlik: false },
];
/** ROWS başlığı (uzun biçim, dört tablo; WPS ve boya ile aynı biçim). */
export const PROJE_OKUMA_SATIR_BASLIKLARI = ["Table", "Row", "Field", "Value", "As written", "Page", "Certainty"];
export const PROJE_BOLUM_TURLERI = ["coc", "dnv survey", "part"];
/** Hat → Document alanı (karşılaştırma sırası: Choke, Kill, Booster, Hydraulic). */
export const PROJE_HAT_ALANLARI = [["Choke", "hat_sn_choke"], ["Kill", "hat_sn_kill"], ["Booster", "hat_sn_booster"], ["Hydraulic", "hat_sn_hydraulic"]];
export const PROJE_HATLARI = PROJE_HAT_ALANLARI.map(([h]) => h);
/** Alanın ekran adı (Document, Sections, Parts, Marks sırasıyla aranır; Parts ve Marks'ın "SN" / "Line"ı tabloyla birlikte gösterilir); bilinmiyorsa alanın kendisi. */
export const projeOkumaEtiketi = (alan) => [...PROJE_OKUMA_ALANLARI, ...PROJE_BELGE_ALANLARI, ...PROJE_BOLUM_ALANLARI, ...PROJE_PARCA_SN_ALANLARI, ...PROJE_ISARET_ALANLARI]
    .find((a) => a.alan === alan)?.etiket ?? alan;
/* ---------------- riser no ve yer önerisi (M15-10) ---------------- */
/** Riser no'sunun sayı kısmı, baştaki sıfırlar atılmış ("TAG 0998" → "998"); rakamsızsa null (sunucunun `riserSayisi`'nin aynısı). */
export function projeRiserSayisi(x) {
    const m = /\d+/.exec(x ?? "");
    return m ? m[0].replace(/^0+(?=\d)/, "") : null;
}
/** Eksik riser için bekleme adı ("997" → "R997"; 3 haneye tamamlanır). */
export const projeBeklemeRn = (sayi) => `R${sayi.padStart(3, "0")}`;
/** Riser kapsamlı Project documents yeri. */
export const projeRiserYeri = (rn, belgeTuru) => ({ tur: "proje", kapsam: "riser", anahtar: rn, belgeTuru });
/** M15-27: iki proje yeri aynı riser mi (kapsam riser ve anahtar eşit; belge türü karşılaştırılmaz, satırın türü ayrıca denetlenir). */
export const projeYerEsit = (a, b) => Boolean(a && b && a.tur === "proje" && b.tur === "proje" && a.kapsam === "riser" && b.kapsam === "riser" && a.anahtar && a.anahtar === b.anahtar);
/**
 * M15-10 (saf; boya M14-8 kalıbı, kuyruk yok): (1) kayıtlı riser yeri → `kisi`; (2) kesin riser no OASIS'te → `oneri`, değilse `riser_bekliyor`; (3) riser no boş ya da şüpheli: kapak SN'si
 * MRB kütüğünde bir riserinse o (not `kapak_sn`), değilse CoC SN'leri tek riseri gösteriyorsa o (not `sn_riseri`); birden çok riser → `sn_baska`; hiçbiri → `supheli` ya da `bos`.
 * OASIS yeni riser açmaz.
 */
export function projeYerOnerisi(g) {
    const notlar = [];
    const k = g.kayitli;
    if (k && k.tur === "proje" && k.kapsam === "riser" && k.anahtar) {
        return { durum: "kisi", yer: projeRiserYeri(k.anahtar, g.belgeTuru), neden: null, bekleme: null, notlar };
    }
    const r = g.riserNo;
    const dolu = Boolean(r && r.kesinlik !== "bos" && r.deger && r.deger.trim());
    const supheli = dolu && r.kesinlik === "suphe";
    if (dolu && !supheli) {
        const sayi = projeRiserSayisi(r.deger);
        if (sayi) {
            const rn = g.riserler.get(sayi);
            if (rn)
                return { durum: "oneri", yer: projeRiserYeri(rn, g.belgeTuru), neden: null, bekleme: null, notlar };
            return { durum: "riser_bekliyor", yer: null, neden: null, bekleme: { rn: projeBeklemeRn(sayi) }, notlar };
        }
        notlar.push({ tur: "bicim", alan: "riser_no", deger: r.deger });
    }
    if (g.riserSn) {
        const rn = g.mrbSnRiseri.get(g.riserSn);
        if (rn) {
            notlar.push({ tur: "kapak_sn", rn });
            return { durum: "oneri", yer: projeRiserYeri(rn, g.belgeTuru), neden: null, bekleme: null, notlar };
        }
    }
    const snRiserleri = [...new Set(g.snler.flatMap((s) => g.snRiseri(s)))].sort();
    if (snRiserleri.length === 1) {
        notlar.push({ tur: "sn_riseri", rn: snRiserleri[0] });
        return { durum: "oneri", yer: projeRiserYeri(snRiserleri[0], g.belgeTuru), neden: null, bekleme: null, notlar };
    }
    if (snRiserleri.length > 1)
        return { durum: "yok", yer: null, neden: "sn_baska", bekleme: null, notlar };
    return { durum: "yok", yer: null, neden: supheli ? "supheli" : "bos", bekleme: null, notlar };
}
/** Okuma durumunun karşılığı (M15-10 `gelenDurumu`): kisi / oneri / proje → okundu. */
export const projeGelenDurumu = (d) => d === "riser_bekliyor" ? "riser_bekliyor" : d === "yok" ? "yer_bekliyor" : "okundu";
/* ---------------- rev sırası (M15-17) ---------------- */
/**
 * M15-17 (saf): ikisi de (`kimlikSade` sonrası, isteğe bağlı "REV" / "REV." önekiyle) sayıysa sayısal; ikisi de tek harfse harf sırası; aksi hâlde null (karşılaştırılamaz). Tahmin yok.
 */
export function projeRevSirasi(a, b) {
    const x = kimlikSade(a);
    const y = kimlikSade(b);
    if (!x || !y)
        return null;
    const sayi = /^(?:REV\.?)?0*(\d+)$/;
    const harf = /^(?:REV\.?)?([A-Z])$/;
    const sx = sayi.exec(x);
    const sy = sayi.exec(y);
    if (sx && sy) {
        const p = Number(sx[1]);
        const q = Number(sy[1]);
        return p < q ? -1 : p > q ? 1 : 0;
    }
    const hx = harf.exec(x);
    const hy = harf.exec(y);
    if (hx && hy)
        return hx[1] < hy[1] ? -1 : hx[1] > hy[1] ? 1 : 0;
    return null;
}
/* ---------------- sayfalar (M15-15) ---------------- */
/** Pages metni → aralık: boş → null (dosyanın tamamı); "9" ya da "1-4"; başka yazım undefined. */
export function projeSayfaMetniOku(metin) {
    const s = (metin ?? "").trim();
    if (!s)
        return null;
    const m = /^(\d+)(?:\s*[-–]\s*(\d+))?$/.exec(s);
    if (!m)
        return undefined;
    const bas = Number(m[1]);
    const son = Number(m[2] ?? m[1]);
    return bas >= 1 && son >= bas ? { bas, son } : undefined;
}
/** Aralığın metni ("3-4"; tek sayfa "9"). */
export const projeAralikMetni = (a) => (a.bas === a.son ? String(a.bas) : `${a.bas}-${a.son}`);
/** Pages (null = dosyanın tamamı) bir PB-01 bölümüyle kesişiyorsa o bölüm; değilse null. Web ve sunucu aynı kuralı okur. */
export function projeYabanciKesisim(sayfalar, yabanci, sayfaSayisi) {
    const a = sayfalar ?? { bas: 1, son: Math.max(1, sayfaSayisi) };
    return yabanci.find((y) => a.bas <= y.son && y.ilk <= a.son) ?? null;
}
export const PROJE_YABANCI_SAYFA_METNI = "Pages {0}–{1} are riser {2}'s {3}: they are not filed for this riser. Change Pages.";
export const PROJE_IKINCI_TEKIL_METNI = "{0} already has a {1} on Project documents. Remove it there first if this one replaces it.";
/** `{0}` yer tutucularını doldurur (sunucu ret cümlesi; web `cevir` ile aynı kalıbı çevirir). */
export const projeKalipDoldur = (kalip, ...d) => kalip.replace(/\{(\d+)\}/g, (_, i) => String(d[Number(i)] ?? ""));
/** Datapack satırının öteki türü (osi_coc ↔ dnv_survey_report); başka türde null. */
export const projeOtekiTur = (t) => (t === "osi_coc" ? "dnv_survey_report" : t === "dnv_survey_report" ? "osi_coc" : null);
/** Saf (M15-18): kartın durumu ve yazımın ön denetimi aynı kuraldır; ilk tutan döner. `engel` yalnız "engel"de dolu. */
export function projeIkinciDurumu(d) {
    const h = d.hedef && d.hedef.tur === "proje" ? d.hedef : null;
    const tur = h ? projeOtekiTur(h.belgeTuru) : null;
    const sayfalar = tur === "dnv_survey_report" ? d.oneri.dnv : tur === "osi_coc" ? d.oneri.coc : null;
    const ol = (durum, engel = null) => ({ durum, engel, tur, sayfalar });
    if (!d.okumaDatapack)
        return ol("yok");
    if (!d.dosyalandi)
        return ol("dosyalanmadi");
    if (!h || h.kapsam !== "riser" || !h.anahtar || !tur)
        return ol("yok");
    if (!d.okumaGuncel || !d.yerEsit)
        return ol("engel", d.okumaDegistiMetni);
    if (!sayfalar)
        return ol("oneri_yok");
    if (d.eklenen)
        return ol("eklendi");
    if (d.tekilVar)
        return ol("engel", PROJE_IKINCI_TEKIL_METNI);
    if (d.engel)
        return ol("engel", d.engel);
    return ol("eklenebilir");
}
/* ---------------- uyarılar ---------------- */
/**
 * Kartta üretilen kodlar ve sırası (M15-22, plan P:651 + §3.1). K6 (HT-01) kapalıdır ve basınç okunmaz; KIM-03, GEN-02, GEN-10 bu kartta üretilmez. Hiçbiri KAPALI_UYARI_KODLARI'nda olamaz (test).
 */
export const PROJE_UYARI_KODLARI = [
    "PB-01", "KIM-01", "KIM-02", "KIM-07", "KIM-08", "KIM-04", "PB-02", "PB-03", "PB-05", "GEN-13", "KIM-05", "KIM-06", "PB-04", "GEN-11", "GEN-04", "GEN-05", "GEN-09",
];
/** Uyarı kalıpları (İngilizce anahtar; Türkçesi sözlükte). KIM, GEN-04, GEN-05, GEN-09 ve GEN-11 öteki okuyucularınkiyle aynıdır. */
export const PROJE_UYARI_KALIPLARI = {
    "PB-01": "Pages {0}–{1} are riser {2}'s {3}. Exclude them or reference riser {2}'s own report.",
    "KIM-01": HIDROTEST_UYARI_KALIPLARI["KIM-01"],
    "KIM-02": HIDROTEST_UYARI_KALIPLARI["KIM-02"],
    "KIM-07": HIDROTEST_UYARI_KALIPLARI["KIM-07"],
    "KIM-08": HIDROTEST_UYARI_KALIPLARI["KIM-08"],
    "KIM-04": HIDROTEST_UYARI_KALIPLARI["KIM-04"],
    "PB-02": "DNV sub-assembly SN {0} differs from OASIS {1}.",
    "PB-03": "Not signed: For DNV review version.",
    "PB-05": "DVR {0} referenced on the survey report is not on file.",
    "GEN-13": { coc: "No signature in the OSI Certificate of Conformance.", dnv: "No signature of the surveyor on the DNV survey report." },
    "KIM-05": HIDROTEST_UYARI_KALIPLARI["KIM-05"],
    "KIM-06": NDT_UYARI_KALIPLARI["KIM-06"],
    "PB-04": "Next complete survey due on {0}.",
    "GEN-11": WPS_BELGESI_UYARI_KALIPLARI["GEN-11"],
    "GEN-04": KALIBRASYON_UYARI_KALIPLARI["GEN-04"],
    "GEN-05": UYARI_KALIPLARI["GEN-05"],
    "GEN-09": NDT_UYARI_KALIPLARI["GEN-09"],
};
/** M15-25: PB-01'in seçenekleri ("Move" yok, K40). */
export const PB01_SECENEKLERI = ["sayfalari_disla", "riser_raporuna_bagla", "datapack_reddet"];
/** PB-01 "Reference" seçeneğinin kanıt türü (x riserinin okunmuş kendi DNV raporu aynı no ve rev'li). */
export const PROJE_AYNI_RAPOR_KANITI = "proje_ayni_rapor";
/** Bölümün ekran adı (PB-01 {3}, kart): coc → "Certificate of Conformance", dnv survey → "DNV survey report", part → başlık ya da "report". */
export const projeBolumAdi = (tur, baslik) => tur === "coc" ? "Certificate of Conformance" : tur === "dnv survey" ? "DNV survey report" : baslik?.trim() || "report";
//# sourceMappingURL=proje-okuma.js.map